import { useParams, useNavigate } from "react-router-dom";
import useProducts from "../../hooks/useProducts";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useUser } from "../../hooks/useUser";
import { useEffect } from "react";
import SafeImage from "../../components/common/SafeImage";

export default function VendorProductDetail() {
  const { vendor_product_id } = useParams();
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { getVendorProductDetailById } = useProducts();
  const { data: vendorProductResult, isLoading: loadingVendorProduct } =
    getVendorProductDetailById(vendor_product_id);

  if (
    !loadingVendorProduct &&
    (!vendorProductResult || vendorProductResult.status !== "success")
  ) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <div className="alert alert-danger">
                  <h4>Vendor Product Not Found</h4>
                  <p>The vendor product you're looking for doesn't exist.</p>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => navigate("/vendor-products")}
                  >
                    Back to Vendor Products
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  // Get product data
  const productData = vendorProductResult?.data;

  //Get veriants
  const variants = productData?.product_variables || [];

  // Count of variants
  const hasVariants = variants.length > 0;

  // merge variant + vendor data
  const mergedVariants = (productData?.product_variables || []).map(v => {
    const vp = productData?.vendor_products?.find(p => p.sku_id === v.sku_id);

    return {
      ...v,
      price: vp?.price ?? null,
      stock: vp?.stock ?? 0,
      tax_value: vp?.tax_value ?? 0,
      commission_percent: vp?.commission_percent ?? 0,
      currency_symbol: vp?.currency_symbol ?? productData?.vendor_products?.[0]?.currency_symbol ?? "",
      is_available: !!vp, // true if vendor has this variant
    };
  });


  //If not has variants and product vendor products data lenth
  const hasSingleProduct =
    !hasVariants && productData?.vendor_products.length > 0;

  //  products
  const Vendor_Product = hasSingleProduct
    ? productData?.vendor_products?.[0]
    : (productData?.vendor_products ?? []);

  // Check if category is Foods
  const isFoodCategory = productData?.category?.category_name === "Foods";

  // Helper to parse available_days JSON string
  const parseAvailableDays = (daysStr) => {
    if (!daysStr) return [];
    try {
      return JSON.parse(daysStr);
    } catch (e) {
      return [];
    }
  };

  // Construct availability object
  const availability = {
    is_available: true, // default true, Foods always available
    status: "available",
    // message: "Available All Time",
    availability_time: {
      start: productData?.vendor_products?.[0]?.available_from || "00:00",
      end: productData?.vendor_products?.[0]?.available_to || "23:59",
    },
    // Parse available_days
    available_days: (() => {
      const vpDays = parseAvailableDays(
        productData?.vendor_products?.[0]?.available_days
      );
      const enabledDays = vpDays.filter((d) => d.Isenable);
      return enabledDays.length
        ? enabledDays.map((d) => d.title).join(", ")
        : "All Days"; // fallback if nothing enabled
    })(),
    current_time: null,
  };



  const formatMoney = (amount, currencySymbol) => {
    const val = amount ?? "";
    const sym = (currencySymbol ?? "").toString().trim();

    // currency_symbol null/empty -> price மட்டும்
    if (!sym) return `${val}`;

    // currency_symbol இருந்தா -> "LKR 45.00" மாதிரி
    return `${sym} ${val}`;
  };

  return loadingVendorProduct ? (
    <LoadingSpinner />
  ) : (
    <div className="container py-4">
      <h2 className="mb-4 text-primary">Vendor Product Details</h2>
      <div className="row g-4">
        {/* Info Card */}
        <div className="col-md-8 offset-md-2">
          <div className="card shadow-sm rounded-4">
            <div className="card-body">
              {/* Product Image */}
              {JSON.parse(productData?.product_image || [])?.length > 0 && (
                <div className="row mb-4">
                  <div className="col-sm-4 fw-semibold text-muted">
                    Product Image
                  </div>
                  <div className="col-sm-8">
                    <SafeImage
                      src={(() => {
                        try {
                          return JSON.parse(
                            productData.product_image || [],
                          )?.[0];
                        } catch (e) {
                          return productData.product_image;
                        }
                      })()}
                      alt={productData.product_name}
                      className="img-fluid rounded"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Product Name
                </div>
                <div className="col-sm-8">{productData?.product_name}</div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Description
                </div>
                <div
                  className="col-sm-8"
                  dangerouslySetInnerHTML={{
                    __html: productData?.description || "",
                  }}
                ></div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">Category</div>
                <div className="col-sm-8">
                  {productData?.category?.category_name}
                  {productData?.category?.category_image && (
                    <div className="mt-1">
                      <SafeImage
                        src={
                          productData.category.category_image.startsWith("http")
                            ? productData.category.category_image
                            : `${window.location.origin}${productData.category.category_image}`
                        }
                        alt={productData.category.category_name}
                        className="img-fluid rounded"
                        style={{
                          maxWidth: "50px",
                          maxHeight: "50px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {productData?.subcategory && (
                <div className="row mb-3">
                  <div className="col-sm-4 fw-semibold text-muted">
                    Subcategory
                  </div>
                  <div className="col-sm-8">
                    {productData?.subcategory?.subcategory_name}
                    {productData?.subcategory?.subcategory_image && (
                      <div className="mt-1">
                        <SafeImage
                          src={
                            productData.subcategory.subcategory_image.startsWith(
                              "http",
                            )
                              ? productData.subcategory.subcategory_image
                              : `${window.location.origin}${productData.subcategory.subcategory_image}`
                          }
                          alt={productData.subcategory.subcategory_name}
                          className="img-fluid rounded"
                          style={{
                            maxWidth: "50px",
                            maxHeight: "50px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">Status</div>
                <div className="col-sm-8">
                  <span
                    className={`badge ${productData?.status === "active" ? "bg-success" : "bg-danger"}`}
                  >
                    {productData?.status}
                  </span>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4 fw-semibold text-muted">
                  Age Restricted
                </div>
                <div className="col-sm-8">
                  <span
                    className={`badge ${productData?.age_validation_flag ? "bg-warning" : "bg-success"}`}
                  >
                    {productData?.age_validation_flag ? "Yes" : "No"}
                  </span>
                </div>
              </div>

              {productData?.sku_id && (
                <div className="row mb-3">
                  <div className="col-sm-4 fw-semibold text-muted">SKU ID</div>
                  <div className="col-sm-8">{productData?.sku_id}</div>
                </div>
              )}

              {/* Product Pricing and Availability */}
              {Vendor_Product?.price && (
                <div className="row mb-3">
                  <div className="col-sm-4 fw-semibold text-muted">Price</div>
                  <div className="col-sm-8">
                    {" "}
                    {formatMoney(
                      Vendor_Product?.price,
                      Vendor_Product?.currency_symbol,
                    )}
                  </div>
                </div>
              )}

              {/* Product Pricing and Availability */}
              {Vendor_Product?.commission_percent > 0 && (
                <div className="row mb-3">
                  <div className="col-sm-4 fw-semibold text-muted">
                    Commission percent
                  </div>
                  <div className="col-sm-8">
                    {Vendor_Product?.commission_percent} %
                  </div>
                </div>
              )}

              {/* Tax value */}
              {Vendor_Product?.tax_value && (
                <div className="row mb-3">
                  <div className="col-sm-4 fw-semibold text-muted">
                    Tax value percentage
                  </div>
                  <div className="col-sm-8">
                    {/* {formatMoney(
                      Vendor_Product?.tax_value,
                      Vendor_Product?.currency_symbol,
                    )} */}
                    {Vendor_Product?.tax_value} %
                  </div>
                </div>
              )}
              {Vendor_Product?.stock  && (
                <div className="row mb-3">
                  {!isFoodCategory &&
                  <div className="col-sm-4 fw-semibold text-muted">
                    In Stock
                  </div>
                  }
                  <div className="col-sm-8">
                    {Vendor_Product?.stock} 
                  </div>
                </div>
              )}
              {/* Availability Information */}
              {isFoodCategory && (
                <>
                    {/* Availability Info */}
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-semibold text-muted">Availability Status</div>
                      <div className="col-sm-8">
                        <span
                          className={`badge ${availability.is_available ? "bg-success" : "bg-warning"}`}
                        >
                          {availability.status === "available" ? "Available Now" : "Not Available"}
                        </span>
                        <div className="mt-1 text-muted small">{availability.message}</div>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-sm-4 fw-semibold text-muted">Available Hours</div>
                      <div className="col-sm-8">
                        {availability.availability_time?.start} - {availability.availability_time?.end}
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-sm-4 fw-semibold text-muted">Available Days</div>
                      <div className="col-sm-8">{availability.available_days}</div>
                    </div>
                </>
              )}

              {/* Stock Information */}
              {productData?.stock && (
                <>
                  <div className="row mb-3">
                    <div className="col-sm-4 fw-semibold text-muted">
                      Stock Quantity
                    </div>
                    <div className="col-sm-8">{productData.stock.quantity}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-sm-4 fw-semibold text-muted">
                      Batch Number
                    </div>
                    <div className="col-sm-8">
                      {productData.stock.batch_number}
                    </div>
                  </div>
                  {productData.stock.mfd_date && (
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-semibold text-muted">
                        Manufacturing Date
                      </div>
                      <div className="col-sm-8">
                        {new Date(
                          productData.stock.mfd_date,
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                  {productData.stock.expire_date && (
                    <div className="row mb-3">
                      <div className="col-sm-4 fw-semibold text-muted">
                        Expire Date
                      </div>
                      <div className="col-sm-8">
                        {new Date(
                          productData.stock.expire_date,
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Show variants if available */}
              {hasVariants && (
                <div className="row mb-3">
                  <div className="col-sm-4 fw-semibold text-muted">
                    Product Variants
                  </div>
                  <div className="col-sm-8">
                    <div className="d-flex flex-column gap-3">
                      {mergedVariants
                        ?.filter(variant => variant.is_available) // only show available variants
                        .map((variant, idx) => (
                          <div
                            key={variant.sku_id || idx}
                            className="variant-card border rounded-4 p-3 bg-white shadow-sm position-relative"
                            style={{
                              borderColor: "#e9ecef",
                              transition: "box-shadow 0.2s",
                              cursor: "pointer",
                              minHeight: 100,
                            }}
                            onMouseOver={(e) =>
                              (e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.08)")
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)")
                            }
                          >
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              {/* Variant Image */}
                              {variant.image_url && (
                                <SafeImage
                                  src={variant.image_url}
                                  alt={variant.variable_name}
                                  className="rounded"
                                  style={{
                                    width: "60px",
                                    height: "60px",
                                    objectFit: "cover",
                                    border: "2px solid #e9ecef",
                                  }}
                                />
                              )}

                              <span
                                className="badge"
                                style={{
                                  background: "#f1f3f4",
                                  color: "#333",
                                  fontWeight: 600,
                                  minWidth: 90,
                                  letterSpacing: 1,
                                  fontSize: "1rem",
                                  padding: "0.5em 1em",
                                }}
                              >
                                {variant.sku_id || `Variant ${idx + 1}`}
                              </span>
                              <span
                                className="badge"
                                style={{
                                  background: "#5679ec96",
                                  color: "#222",
                                  fontSize: "1rem",
                                  fontWeight: 600,
                                  padding: "0.5em 1em",
                                }}
                              >
                                {variant.variable_name}
                              </span>
                              {!isFoodCategory && (
                                <span
                                  className="badge"
                                  style={{
                                    background: "#84e77b7c",
                                    color: "#e23535",
                                    fontWeight: 500,
                                    fontSize: "1rem",
                                    padding: "0.5em 1em",
                                  }}
                                >
                                  In Stock: {variant.stock} 
                                </span>
                              )}
                              <span
                                className="badge"
                                style={{
                                  background: "#e6f4ea",
                                  color: "#137333",
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                  padding: "0.5em 1em",
                                }}
                              >
                                {formatMoney(variant.price, variant.currency_symbol)}
                              </span>
                              <span
                                className="badge"
                                style={{
                                  background: "#e3f2fd",
                                  color: "#1565c0",
                                  fontWeight: 500,
                                  fontSize: "1rem",
                                  padding: "0.5em 1em",
                                }}
                              >
                                Tax: {variant.tax_value} %
                              </span>
                              <span
                                className="badge"
                                style={{
                                  background: "#fdede3",
                                  color: "#1565c0",
                                  fontWeight: 500,
                                  fontSize: "1rem",
                                  padding: "0.5em 1em",
                                }}
                              >
                                Commission: {variant.commission_percent} %
                              </span>
                            </div>
                          </div>
                      ))}

                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end gap-3 mt-4">
                <button
                  onClick={() => {
                    if (user?.role === "vendor") {
                      navigate("/product");
                    } else if (
                      user?.role === "admin" ||
                      user?.role === "employee"
                    ) {
                      navigate(
                        `/product?vendor_id=${productData?.vendor?.vendor_id}`,
                      );
                    } else {
                      navigate("/product");
                    }
                  }}
                  className="btn btn-sm btn-outline-danger"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
